/**
 * Video card.
 *
 * Rest state is a poster with a play button. On a fine pointer the clip plays
 * muted as a silent preview on hover; clicking commits to playback with sound
 * and controls. Externally hosted clips swap in their iframe only on click, so
 * nothing third-party loads until someone asks for it.
 */
class RanaVideoCard extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.embedUrl = this.getAttribute('data-embed');
    this.trigger = this.querySelector('[data-video-play]');
    this.playing = false;

    this.trigger?.addEventListener('click', () => this.#activate());

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.video && canHover && !reduced && this.hasAttribute('data-preview')) {
      this.addEventListener('pointerenter', this.#preview);
      this.addEventListener('pointerleave', this.#stopPreview);
    }
  }

  /** Silent preview — never with sound, and never once committed. */
  #preview = () => {
    if (this.playing) return;
    this.video.play().catch(() => {
      // Autoplay can be refused; the poster simply stays.
    });
  };

  #stopPreview = () => {
    if (this.playing || !this.video) return;
    this.video.pause();
    this.video.currentTime = 0;
  };

  #activate() {
    this.playing = true;
    this.classList.add('is-playing');

    if (this.video) {
      this.video.muted = false;
      this.video.controls = true;
      this.video.loop = false;
      this.video.play().catch(() => {});
      return;
    }

    if (!this.embedUrl) return;

    const frame = document.createElement('iframe');
    frame.src = this.embedUrl;
    frame.title = this.getAttribute('data-title') || 'Video';
    frame.loading = 'lazy';
    frame.allow = 'autoplay; fullscreen; picture-in-picture';
    frame.allowFullscreen = true;
    frame.className = 'rana-vid__frame';
    this.querySelector('[data-video-slot]')?.replaceChildren(frame);
  }
}

if (!customElements.get('rana-video-card')) {
  customElements.define('rana-video-card', RanaVideoCard);
}
